"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";

const values = [
{
  icon: "LightBulbIcon",
  title: "Innovation-First",
  description: "We combine data-driven insights with creative problem-solving to deliver solutions that go beyond the obvious."
},
{
  icon: "HandshakeIcon",
  title: "Partnership Mindset",
  description: "We treat every client as a long-term partner, aligning our success with yours."
},
{
  icon: "ShieldCheckIcon",
  title: "Operational Integrity",
  description: "Transparent communication and accountable execution are non-negotiable in everything we do."
},
{
  icon: "ChartBarIcon",
  title: "Results-Driven",
  description: "Every strategy is measured against real KPIs — we optimize until the numbers reflect the goal."
}];


const globalOffices = [
  { label: "Headquarters", address: "1100 Peachtree Street NE, Suite 2800", city: "Atlanta, Georgia 30309", country: "USA", flag: "🇺🇸" },
  { label: "European Office", address: "Calea Victoriei 155, Sector 1", city: "Bucharest 010073", country: "Romania", flag: "🇷🇴" },
  { label: "Pacific Office", address: "1 Martin Place, Level 18", city: "Sydney NSW 2000", country: "Australia", flag: "🇦🇺" },
  { label: "Asian Office", address: "1 Raffles Place, #40-01", city: "Singapore 048616", country: "Singapore", flag: "🇸🇬" },
  { label: "North American Operations", address: "100 King Street West, Suite 5600", city: "Toronto, ON M5X 1C9", country: "Canada", flag: "🇨🇦" },
];


export default function WhoWeAreSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const el = sectionRef.current;
    if (el) el.querySelectorAll(".reveal, .reveal-scale").forEach((i) => observer.observe(i));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-28 bg-[#0C1B33]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Who We Are */}
        <div className="grid lg:grid-cols-12 gap-16 items-center mb-24">
          <div className="lg:col-span-5 space-y-6">
            <div className="reveal">
              <p className="text-[11px] font-display uppercase tracking-[0.2em] text-gold mb-3">
                Who We Are
              </p>
              <h2 className="text-4xl md:text-5xl font-display font-700 text-cream leading-tight tracking-tight">
                A Different Kind of{" "}
                <span className="text-gold">Operations Partner</span>
              </h2>
            </div>
            <p className="reveal delay-100 text-[17px] font-body text-cream/60 leading-relaxed">
              StanDave LLC was built on a simple belief: operational excellence should be accessible to every business, regardless of size or industry. We partner with organizations to enhance operational efficiency, reduce costs, and implement scalable systems that drive long-term growth.
            </p>
            <p className="reveal delay-200 text-[17px] font-body text-cream/60 leading-relaxed">
              Our approach combines data-driven insights, financial planning expertise, and hands-on operational strategies tailored to each client&apos;s unique needs — not off-the-shelf templates.
            </p>

            <div className="reveal delay-300 grid grid-cols-2 gap-4 pt-4">
              {[
              { value: "97+", label: "Engagements" },
              { value: "15+", label: "Countries" },
              { value: "38%", label: "Avg. Cost Savings" },
              { value: "12yr", label: "Experience" }].
              map((stat) =>
              <div
                key={stat.label}
                className="glass-card rounded-xl p-4 text-center">
                
                  <div className="text-2xl font-display font-700 text-gold">
                    {stat.value}
                  </div>
                  <div className="text-[11px] font-display uppercase tracking-widest text-muted mt-1">
                    {stat.label}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7 reveal-scale delay-200 relative hidden lg:block">
            <div className="relative rounded-2xl overflow-hidden h-[480px]">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_122c7bcd2-1772180359189.png"
                alt="Business professionals in a collaborative meeting discussing operational strategy and supply chain solutions"
                fill
                className="object-cover" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#0C1B33] via-transparent to-transparent opacity-60" />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#0C1B33] opacity-30" />
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div>
          <div className="text-center mb-12">
            <p className="reveal text-[11px] font-display uppercase tracking-[0.2em] text-gold mb-3">
              What Guides Us
            </p>
            <h2 className="reveal delay-100 text-3xl md:text-4xl font-display font-700 text-cream tracking-tight">
              Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((val, i) =>
            <div
              key={val.title}
              className={`reveal delay-${(i + 1) * 100} service-card rounded-2xl p-7 group`}>
              
                <div className="w-10 h-10 rounded-lg bg-[rgba(201,168,76,0.1)] flex items-center justify-center mb-5 group-hover:bg-[rgba(201,168,76,0.2)] transition-colors">
                  <Icon
                  name={val.icon as "LightBulbIcon"}
                  size={18}
                  className="text-gold" />
                
                </div>
                <h3 className="text-[17px] font-display font-700 text-cream mb-3 group-hover:text-gold transition-colors">
                  {val.title}
                </h3>
                <p className="text-[14px] font-body text-cream/55 leading-relaxed">
                  {val.description}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Global Offices */}
        <div className="mt-24">
          <div className="text-center mb-12">
            <p className="reveal text-[11px] font-display uppercase tracking-[0.2em] text-gold mb-3">Our Presence</p>
            <h2 className="reveal delay-100 text-3xl md:text-4xl font-display font-700 text-cream tracking-tight">Global Offices</h2>
            <p className="reveal delay-200 text-[16px] font-body text-cream/50 mt-4 max-w-xl mx-auto">Strategically located across five continents to serve our clients wherever business takes them.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {globalOffices.map((office, i) => (
              <div key={office.label} className={`reveal delay-${(i + 1) * 100} service-card rounded-2xl p-6 group`}>
                <div className="text-3xl mb-4">{office.flag}</div>
                <div className="text-[9px] font-display uppercase tracking-widest text-gold/70 mb-1">{office.label}</div>
                <p className="text-[14px] font-body text-cream/80 leading-relaxed mb-1">{office.address}</p>
                <p className="text-[13px] font-body text-cream/55">{office.city}</p>
                <p className="text-[11px] font-display uppercase tracking-wider text-gold/50 mt-2">{office.country}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>);

}